import { defineMessages } from "./define.js";

/**
 * The calendar: the agenda, the month grid, the event form.
 *
 * Dates themselves are not here — they come from `formats.ts` through Intl, so
 * only the words around them need a translation. What is here is the named days
 * («сегодня», «завтра», «вчера») in lower case, because the headings that show
 * them capitalise the first letter in CSS.
 */
export const calendar = defineMessages("calendar", {
  subtitle: { ru: "Что запланировано.", en: "What's planned." },
  fieldUrl: { ru: "Ссылка", en: "Link" },
  urlHint: {
    ru: "Созвон, билеты, карта — что открыть перед событием",
    en: "A call, tickets, a map — whatever to open before it",
  },
  errorUrl: { ru: "Это не похоже на ссылку", en: "That does not look like a link" },
  openLink: { ru: "Открыть ссылку", en: "Open the link" },

  viewLabel: { ru: "Вид календаря", en: "Calendar view" },
  /* Третий вид чтения, а не четвёртый масштаб: «Ближайшее» отвечает на «что
     впереди», календарь — на «свободно ли 14-е», а это — на вопрос, с которым
     утром открывают приложение. И это не та же лента, обрезанная по дню: сюда
     приходят сроки дел, которые живут на другом экране. */
  viewToday: { ru: "Сегодня", en: "Today" },
  todayNothing: {
    ru: "На сегодня ничего не назначено.",
    en: "Nothing on for today.",
  },
  todayEvents: { ru: "События", en: "Events" },
  todayDue: { ru: "Сроки", en: "Deadlines" },
  todayOverdue: { ru: "просрочено", en: "overdue" },
  todayInList: { ru: "в списке «{title}»", en: "in “{title}”" },
  viewUpcoming: { ru: "Ближайшее", en: "Upcoming" },
  zoomLabel: { ru: "Насколько подробно", en: "How close to look" },
  viewDay: { ru: "День", en: "Day" },
  viewWeek: { ru: "Неделя", en: "Week" },
  viewMonth: { ru: "Месяц", en: "Month" },

  newEvent: { ru: "Новое событие", en: "New event" },

  loading: { ru: "Загружаем календарь…", en: "Loading the calendar…" },
  empty: {
    ru: "Пока ничего не запланировано. Добавьте первое событие — и оно тут же появится у второго.",
    en: "Nothing planned yet. Add the first event — it shows up for the other one right away.",
  },
  nothingAhead: {
    ru: "Впереди пусто. Всё, что было, — ниже.",
    en: "Nothing ahead. Everything that has been is below.",
  },
  pastHeading: { ru: "Уже прошло", en: "Already past" },
  showPast: { ru: "Показать прошедшие", en: "Show past events" },

  today: { ru: "сегодня", en: "today" },
  tomorrow: { ru: "завтра", en: "tomorrow" },
  yesterday: { ru: "вчера", en: "yesterday" },

  allDayUntil: { ru: "весь день, по {date}", en: "all day, through {date}" },

  repeats: { ru: "повторяется", en: "repeats" },

  removeEvent: { ru: "Удалить «{title}»", en: "Delete “{title}”" },
  /* Deleting a repeating event takes the whole series with it, and nothing on
     this screen puts one back. The bin asks once. */
  removeConfirm: {
    ru: "Точно удалить «{title}»?",
    en: "Delete “{title}” for good?",
  },

  /* The sheet that opens on an event: everything the row has no width for —
     what it is, the files hanging off it, the discussion about it, and the one
     day of a repeat that is not happening. */
  details: { ru: "Событие", en: "Event" },
  /** Names both the row that opens the sheet and the sheet itself. */
  detailsOf: { ru: "Событие: «{title}»", en: "Event: “{title}”" },

  /* The sheet has two states and the header says which one is on screen.
     Reading is what a tap gives you; the pencil is the only door into the
     form, and «Готово» is the only way out of it that keeps anything. */
  editEvent: { ru: "Редактировать", en: "Edit" },
  editTitle: { ru: "Изменить событие", en: "Edit event" },
  done: { ru: "Готово", en: "Done" },

  /** Место: the label on the sheet, and the label of the field that sets it. */
  location: { ru: "Где", en: "Where" },
  locationPlaceholder: { ru: "Адрес или место", en: "An address or a place" },
  /** The chosen-days rule, said out loud instead of drawn as seven chips. */
  repeatOnDays: { ru: "Каждую неделю: {days}", en: "Every week on {days}" },

  discuss: { ru: "Обсудить", en: "Discuss" },
  discussionCount: {
    ru: {
      one: "Обсуждение · {count} сообщение",
      few: "Обсуждение · {count} сообщения",
      many: "Обсуждение · {count} сообщений",
      other: "Обсуждение · {count} сообщения",
    },
    en: { one: "Discussion · {count} message", other: "Discussion · {count} messages" },
  },

  /* One occurrence of a repeat, called off. The date is the day being dropped,
     so the button names it rather than leaving «этот раз» to the context. */
  skipDay: { ru: "Не в этот раз, {date}", en: "Not this time, {date}" },
  skipDayHint: {
    ru: "Событие останется — не будет только этого дня.",
    en: "The event stays — only this one day is off.",
  },

  /* Отменённые дни, and the way back. A cancelled occurrence is nowhere on the
     calendar, so the only place «вернуть» can live is on the series itself. */
  skippedHint: {
    ru: "Эти дни отменены. Нажмите, чтобы вернуть.",
    en: "These days are off. Tap one to put it back.",
  },
  restoreDay: { ru: "Вернуть {date}", en: "Put {date} back" },

  /*
   * Asked once, at the moment «Готово» is pressed on an event that repeats, and
   * never for one that happens once — there is nothing to choose between then.
   * «Только этот раз» leaves the series alone and puts a separate event on the
   * day; «Всю серию» is what saving has always done.
   */
  scopeAsk: {
    ru: "Событие повторяется. Изменить только этот день или всю серию?",
    en: "This event repeats. Change this one day, or the whole series?",
  },
  scopeOnce: { ru: "Только этот раз, {date}", en: "Only this time, {date}" },
  scopeSeries: { ru: "Всю серию", en: "The whole series" },

  spansDaysNotice: {
    ru: "Событие идёт до {date}. Здесь редактируется только один день — если сохранить, окончание пропадёт.",
    en: "This event runs until {date}. Only one day can be edited here — saving will drop the end.",
  },

  prevMonth: { ru: "Предыдущий месяц", en: "Previous month" },
  nextMonth: { ru: "Следующий месяц", en: "Next month" },
  dayWithEvents: { ru: "{date}, есть события", en: "{date}, has events" },

  /* Неделя и день: сетка, где час — это высота. The hour scale itself is drawn,
     not spelled — only the controls around it need words. */
  prevWeek: { ru: "Предыдущая неделя", en: "Previous week" },
  nextWeek: { ru: "Следующая неделя", en: "Next week" },
  prevDay: { ru: "Предыдущий день", en: "Previous day" },
  nextDay: { ru: "Следующий день", en: "Next day" },
  openDay: { ru: "Открыть день: {date}", en: "Open the day: {date}" },

  emptyDay: {
    ru: "В этот день ничего не запланировано.",
    en: "Nothing planned for this day.",
  },
  addOn: { ru: "Добавить на {date}", en: "Add on {date}" },

  fieldTitle: { ru: "Название", en: "Title" },
  titlePlaceholder: { ru: "Что происходит", en: "What is happening" },
  fieldDate: { ru: "Дата", en: "Date" },
  fieldAllDay: { ru: "Весь день", en: "All day" },
  fieldStart: { ru: "Начало", en: "Starts" },
  fieldEnd: { ru: "Конец", en: "Ends" },
  fieldNote: { ru: "Заметка", en: "Note" },
  /* Всё после «что и когда» — под этой кнопкой. Форма открывалась десятью
     полями сразу, а календарь, который каждый раз столько спрашивает,
     перестают вести. */
  more: { ru: "Повтор, ссылка, место, заметка", en: "Repeat, link, place, note" },
  /* «За сколько» — свойство человека (§4.7), и это его только перекрывает.
     Существовать оно обязано потому, что видов события у пары ровно два, и
     одно число не обслуживает оба: день рождения за два дня, ужин за полчаса. */
  /* Кто идёт — первый вопрос к строке в общем календаре: её врач, моя встреча
     или наш ужин. «Вдвоём», а не «оба»/«обе»: род по имени не определить. */
  goingBoth: { ru: "вдвоём", en: "both of you" },
  fieldReminder: { ru: "Напомнить", en: "Remind me" },
  reminderHint: {
    ru: "Только для этого события",
    en: "For this event only",
  },
  reminderUsual: { ru: "Как обычно", en: "As usual" },
  notePlaceholder: { ru: "Что важно не забыть", en: "What not to forget" },

  /*
   * Повтор. The options are the six a shared calendar is actually asked for;
   * «Своё правило» is not one of them — see `repeatOwn` below.
   */
  fieldRepeat: { ru: "Повтор", en: "Repeat" },
  repeatNever: { ru: "Не повторяется", en: "Does not repeat" },
  repeatDaily: { ru: "Каждый день", en: "Every day" },
  repeatWeekdays: { ru: "По будням", en: "On weekdays" },
  repeatWeekly: { ru: "Каждую неделю", en: "Every week" },
  repeatMonthly: { ru: "Раз в месяц", en: "Once a month" },
  repeatCustom: { ru: "Свои дни", en: "Chosen days" },

  /* The state an existing rule lands in when this form cannot draw it. */
  repeatOwn: { ru: "Своё правило", en: "Its own rule" },
  repeatOwnHint: {
    ru: "Это событие повторяется по правилу, которое здесь не показать. Оно останется как есть, пока вы не выберете другой вариант.",
    en: "This event repeats by a rule this form cannot show. It stays as it is until you pick something else.",
  },

  repeatDaysLabel: { ru: "Дни недели", en: "Days of the week" },

  errorNoTitle: {
    ru: "Без названия событие не сохранить",
    en: "An event needs a title to be saved",
  },
  errorDate: { ru: "Проверьте дату", en: "Check the date" },
  errorStartTime: { ru: "Укажите время начала", en: "Set a start time" },
  errorEndTime: { ru: "Проверьте время окончания", en: "Check the end time" },
  errorEndsBeforeStart: {
    ru: "Событие не может кончаться раньше, чем начинается",
    en: "An event cannot end before it starts",
  },

  /*
   * Кто идёт. Three answers and nothing else: «может быть» was left out on
   * purpose — for two people it is the answer that decides nothing, and the
   * question the screen is asked is «идём или нет».
   *
   * The statuses are written from the answering person's side («иду»), because
   * the only row anybody can change is their own. The third-person reading
   * («идёт») is what the other member's row shows, and it is a separate key
   * rather than the same word doing two jobs badly in one of the two languages.
   */
  participants: { ru: "Участники", en: "Who is coming" },
  participantsGoing: { ru: "Идут: {count}", en: "{count} going" },
  participantsNobody: { ru: "Пока никто не ответил", en: "Nobody has answered yet" },

  /** The creator, so the row says why they are on the list at all. */
  participantCreator: { ru: "создал событие", en: "created the event" },

  /** Your own three buttons. */
  participateLabel: { ru: "Ваш ответ", en: "Your answer" },
  participateGoing: { ru: "Иду", en: "Going" },
  participateNotGoing: { ru: "Не иду", en: "Not going" },
  participateUnanswered: { ru: "Пока не знаю", en: "Undecided" },

  /** How somebody else's answer reads. */
  participantGoing: { ru: "идёт", en: "going" },
  participantNotGoing: { ru: "не идёт", en: "not going" },
  participantUnanswered: { ru: "не ответил", en: "no answer" },
});
